'use client';
import React, { ComponentProps, LegacyRef, ReactNode, forwardRef, useId, useState } from 'react';
import styles from './Input.module.scss';
import { VALIDITY } from '@/constants';
import cn from 'classnames';
import formatPhoneNumber from '@/packages/string/formatPhoneNumber';
import zipcode from '@/packages/string/zipcode';
import { InvalidFeedback } from '../InvalidFeedback/InvalidFeedback';
import IconShowPassword from '@/assets/icons/IconShowPassword';
import IconHidePassword from '@/assets/icons/IconHidePassword';
import { IconHelpOutline } from '@/assets/icons/IconHelpOutline';

export type InputType = 'text' | 'email' | 'tel' | 'zip' | 'password';

export interface InputProps extends ComponentProps<'input'> {
    type: InputType;
    label: ReactNode;
    ariaLabel?: string;
    invalid?: FieldValidityState;
    invalidMessage?: ReactNode;
    dataCy?: string;
    errorDataCy?: string;
    theme?: string;
    inputWrapperStyles?: string;
    wrapperId?: string;
    noSpacing?: boolean;
    transformValue?: (value: string) => string;
    radioVinId?: () => void;
    radioVinIdExpanded?: boolean;
    radioVinIdButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

export interface InputState {
    value: string;
    isFocused: boolean;
    isFilled: boolean;
}

const extendedTypes = ['zip'];

export const Input = forwardRef(function (
    {
        label,
        ariaLabel,
        invalid,
        invalidMessage,
        id,
        type,
        value,
        onBlur,
        onChange,
        dataCy,
        errorDataCy,
        theme,
        className,
        inputWrapperStyles,
        wrapperId,
        noSpacing,
        transformValue,
        radioVinId,
        radioVinIdExpanded,
        radioVinIdButtonRef,
        'aria-describedby': ariaDescribedBy,
        ...otherProps
    }: InputProps,
    ref: LegacyRef<HTMLInputElement>,
): React.JSX.Element {
    const generatedId = useId();
    const elementId = id ? id : generatedId;
    const errorId = `${elementId}-error`;
    const isPasswordInput = type === 'password';
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const inputType =
        extendedTypes.indexOf(type) === -1 ? (isPasswordInput && isPasswordVisible ? 'text' : type) : 'text';
    const ariaInvalid = invalid === VALIDITY.invalid;
    // Only reference the error when it is rendered, or the input points at a missing id.
    const hasRenderedError = ariaInvalid && Boolean(invalidMessage);
    const describedBy =
        [hasRenderedError ? errorId : undefined, ariaDescribedBy].filter(Boolean).join(' ') || undefined;

    const [state, setState] = useState({
        isFocused: false,
        isFilled: Boolean(value),
        value: value || '',
    });

    const inputContainerClassNames = cn(styles.inputContainer, className, {
        [styles.active]: state.isFocused,
        [styles.filled]: state.isFilled,
        [styles.invalid]: ariaInvalid,
        [styles.emailCapture]: theme === 'emailCapture',
        [styles.noSpacing]: noSpacing,
        [styles.passwordInput]: isPasswordInput,
    });

    const setIsFocused = (focused: boolean) =>
        setState((currentState) => ({ ...currentState, isFocused: focused }));

    const getNormalizedValue = (newValue: string) => {
        let normalizedValue = type === 'tel' ? formatPhoneNumber(newValue) : newValue;

        if (type === 'zip') {
            normalizedValue = zipcode(normalizedValue);
        }

        if (transformValue) {
            normalizedValue = transformValue(normalizedValue);
        }

        return normalizedValue;
    };

    const setValue = (newValue: string) => {
        const normalizedValue = getNormalizedValue(newValue);

        setState((currentState) => ({
            ...currentState,
            value: normalizedValue,
            isFilled: Boolean(normalizedValue),
        }));
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
        setIsFocused(false);

        if (onBlur) {
            onBlur(event);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target as HTMLInputElement;
        const normalizedValue = getNormalizedValue(input.value);

        input.value = normalizedValue;
        setValue(normalizedValue);

        if (onChange) {
            onChange(event);
        }
    };

    return (
        <div className={inputWrapperStyles} id={wrapperId}>
            {invalid && invalidMessage && (
                <InvalidFeedback
                    validState={invalid}
                    errorId={errorId}
                    dataCy={errorDataCy}
                    theme={theme === 'emailCapture' ? 'round' : ''}
                >
                    {invalidMessage}
                </InvalidFeedback>
            )}
            <div className={inputContainerClassNames}>
                <label htmlFor={elementId}>{label}</label>
                <input
                    {...otherProps}
                    id={elementId}
                    ref={ref}
                    aria-label={ariaLabel}
                    aria-invalid={ariaInvalid || undefined}
                    aria-required={otherProps.required || undefined}
                    type={inputType}
                    value={state.value}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    aria-describedby={describedBy}
                    data-cy={dataCy}
                />
                {isPasswordInput && (
                    <button
                        type="button"
                        className={styles.passwordToggle}
                        aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                        aria-pressed={isPasswordVisible}
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    >
                        {isPasswordVisible ? <IconHidePassword /> : <IconShowPassword />}
                    </button>
                )}
                {radioVinId && (
                    <button
                        type="button"
                        ref={radioVinIdButtonRef}
                        className={styles.lookupButton}
                        aria-label="Learn how to find your Radio ID"
                        aria-haspopup="dialog"
                        aria-expanded={radioVinIdExpanded}
                        onClick={radioVinId}
                    >
                        <IconHelpOutline className={styles.lookupIcon} />
                    </button>
                )}
            </div>
        </div>
    );
});

Input.displayName = 'Input';
